import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Moon, Lock, ChevronRight, Check } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const IntegrationCard = () => {
  const navigate = useNavigate();
  const [hasAccess, setHasAccess] = useState(true); // Default to true
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAccess();
  }, []);

  const checkAccess = async () => {
    const token = localStorage.getItem("reclaim_token");
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/integration/access`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setHasAccess(data.has_access);

      // Always try to fetch progress
      try {
        const progressRes = await fetch(`${API_URL}/api/integration/progress`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const progressData = await progressRes.json();
        setProgress(progressData);
      } catch (e) {
        console.log("Could not fetch progress");
      }
    } catch (error) {
      console.error("Error checking integration access:", error);
      // On error, still show the card
      setHasAccess(true);
    } finally {
      setLoading(false);
    }
  };

  // Always show a loading placeholder instead of nothing
  if (loading) {
    return (
      <div className="bg-gradient-to-br from-[#3D3A5A] to-[#4A4670] rounded-3xl p-6 animate-pulse">
        <div className="h-16 bg-white/10 rounded-xl"></div>
      </div>
    );
  }

  const currentNight = progress?.current_night || 1;
  const completedCount = progress?.completed_nights?.length || 0;
  const progressPercent = (completedCount / 21) * 100;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="mb-6"
    >
      <div 
        onClick={() => navigate("/integration")}
        className="bg-gradient-to-br from-[#3D3A5A] via-[#4A4670] to-[#574F80] rounded-2xl p-5 cursor-pointer 
                   hover:shadow-lg hover:shadow-violet-500/20 transition-all duration-300
                   border border-white/10"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-white/15 backdrop-blur-sm flex items-center justify-center">
              <Moon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-heading text-lg text-white">21 Night Integration</h3>
              <p className="text-sm text-white/60">Inner Circle</p>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-white/50" />
        </div>

        {/* Progress */}
        <div className="mb-3">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-white/70">
              Night {currentNight} of 21
            </span>
            <span className="text-sm text-white/90">
              {completedCount} completed
            </span>
          </div>
          <div className="h-2 bg-white/10 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${progressPercent}%` }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="h-full bg-gradient-to-r from-violet-400 to-purple-400 rounded-full"
            />
          </div>
        </div>

        {/* Description */}
        <p className="text-sm text-white/60">
          Reinforce your transformation with subconscious integration
        </p>

        {/* Night indicators */}
        <div className="flex gap-1 mt-4 flex-wrap">
          {Array.from({ length: 21 }, (_, i) => {
            const nightNum = i + 1;
            const isCompleted = progress?.completed_nights?.some(n => n.night === nightNum);
            const isCurrent = nightNum === currentNight;
            
            return (
              <div
                key={nightNum}
                className={`w-3 h-3 rounded-full transition-all ${
                  isCompleted 
                    ? "bg-violet-400" 
                    : isCurrent 
                      ? "bg-violet-300/50 ring-2 ring-violet-400" 
                      : "bg-white/10"
                }`}
              />
            );
          })}
        </div>
      </div>
    </motion.div>
  );
};

export default IntegrationCard;
